import { Box, Container, Stack, Typography } from '@mui/material';
import { landing } from './landingTheme';

/**
 * What the product does, in five cards.
 *
 * @remarks
 * <b>Each one is a feature that exists and shipped.</b> The design this follows led with
 * artificial intelligence and a mascot; this product has none, and a landing page that claimed
 * it would be found out on the first screen somebody opened.
 *
 * What is here instead is the honest answer to "why not just use the system of record": these
 * five are the things it has no field for, which is the reason this product was built at all.
 */
const FEATURES = [
  {
    tone: landing.violetSoft,
    title: 'برنامه‌ریزی و گانت',
    body: 'تاریخ شروع، مدت به روز کاری و وابستگی میان کارها — چیزی که در سامانه مرجع اصلاً وجود ندارد.',
    icon: <CalendarIcon />,
  },
  {
    tone: landing.blue,
    title: 'ظرفیت و بار کاری',
    body: 'ساعت کاری قراردادی، مرخصی و تعطیلات، و دیدنِ اینکه چه کسی بیش از توانش کار دارد.',
    icon: <PeopleIcon />,
  },
  {
    tone: landing.mint,
    title: 'سلسله‌مراتب کار',
    body: 'برنامه، پروژه و فاز بالای هر وظیفه، با بازهٔ برنامه‌ریزی‌شده و پیشرفت واقعی هر کدام.',
    icon: <LayersIcon />,
  },
  {
    tone: landing.cyan,
    title: 'تقویم شمسی سرتاسری',
    body: 'همهٔ تاریخ‌ها شمسی و همهٔ ارقام فارسی، در هر صفحه — بدون تبدیل ذهنی و بدون اشتباه.',
    icon: <GlobeIcon />,
  },
  {
    tone: landing.amber,
    title: 'دسترسی و امنیت',
    body: 'هر کس دقیقاً همان پروژه‌هایی را می‌بیند که در سامانه مرجع به او داده شده؛ نه یکی بیشتر.',
    icon: <ShieldIcon />,
  },
] as const;

export function LandingFeatures() {
  return (
    <Box id="features" sx={{ py: { xs: 8, md: 12 }, backgroundColor: 'rgba(5, 8, 26, 0.55)' }}>
      <Container maxWidth="lg">
        <Stack spacing={2} sx={{ textAlign: 'center', alignItems: 'center', mb: { xs: 5, md: 7 } }}>
          <Typography sx={{ color: landing.violetSoft, fontWeight: 700, fontSize: 14 }}>
            چرا رای‌ویرا؟
          </Typography>

          <Typography
            component="h2"
            sx={{ fontSize: { xs: 27, md: 38 }, fontWeight: 800, letterSpacing: '-0.02em' }}
          >
            ابزاری برای تیم‌های حرفه‌ای
          </Typography>

          <Typography sx={{ color: landing.textMuted, lineHeight: 2, maxWidth: 680, fontSize: { xs: 15, md: 16 } }}>
            کارها همان‌جا می‌مانند که تیم فنی می‌شناسد و هیچ‌چیز دو بار ثبت نمی‌شود. رای‌ویرا
            لایه‌ای است که برنامه‌ریزی، ظرفیت و گزارش را به آن اضافه می‌کند.
          </Typography>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gap: 2,
            // One column on a phone, then two, then all five in a row - the shape the
            // reference design uses at desktop width.
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              lg: 'repeat(5, 1fr)',
            },
          }}
        >
          {FEATURES.map((feature) => (
            <FeatureCard key={feature.title} {...feature} />
          ))}
        </Box>
      </Container>
    </Box>
  );
}

function FeatureCard({
  tone,
  title,
  body,
  icon,
}: {
  tone: string;
  title: string;
  body: string;
  icon: React.ReactNode;
}) {
  return (
    <Stack
      spacing={1.5}
      sx={{
        alignItems: 'center',
        textAlign: 'center',
        p: 3,
        borderRadius: 3,
        border: 1,
        borderColor: landing.border,
        backgroundColor: landing.surface,
        transition: 'border-color 160ms, transform 160ms',

        // A hover that lifts rather than glows: the card stays legible for anybody who
        // arrives on it with a keyboard.
        '&:hover': { borderColor: `${tone}66`, transform: 'translateY(-3px)' },
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 48,
          height: 48,
          display: 'grid',
          placeItems: 'center',
          color: tone,
        }}
      >
        {icon}
      </Box>

      <Typography sx={{ fontWeight: 800, fontSize: 17 }}>{title}</Typography>

      <Typography sx={{ color: landing.textMuted, fontSize: 13.5, lineHeight: 1.95 }}>{body}</Typography>
    </Stack>
  );
}

/* ------------------------------------------------------------------------------------------
 * Icons, drawn inline.
 *
 * Five paths weigh less than one icon font and cannot arrive after the text they label. They
 * inherit `color`, which is what lets each card carry its own accent without a second asset.
 * ---------------------------------------------------------------------------------------- */

const strokeProps = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function CalendarIcon() {
  return (
    <Box component="svg" viewBox="0 0 24 24" sx={{ width: 34, height: 34 }}>
      <rect x="3" y="5" width="18" height="16" rx="3" {...strokeProps} />
      <path d="M3 10h18M8 3v4M16 3v4" {...strokeProps} />
      <path d="M7 14h6M7 17.5h4" {...strokeProps} />
    </Box>
  );
}

function PeopleIcon() {
  return (
    <Box component="svg" viewBox="0 0 24 24" sx={{ width: 34, height: 34 }}>
      <circle cx="9" cy="8" r="3.2" {...strokeProps} />
      <path d="M3.5 19.5a5.5 5.5 0 0 1 11 0" {...strokeProps} />
      <path d="M16 6.2a3 3 0 0 1 0 5.6M17.5 19.5a5.4 5.4 0 0 0-2-4.2" {...strokeProps} />
    </Box>
  );
}

function LayersIcon() {
  return (
    <Box component="svg" viewBox="0 0 24 24" sx={{ width: 34, height: 34 }}>
      <path d="M12 3 21 8l-9 5-9-5 9-5Z" {...strokeProps} />
      <path d="M3.5 12.5 12 17l8.5-4.5M3.5 16.5 12 21l8.5-4.5" {...strokeProps} />
    </Box>
  );
}

function GlobeIcon() {
  return (
    <Box component="svg" viewBox="0 0 24 24" sx={{ width: 34, height: 34 }}>
      <circle cx="12" cy="12" r="9" {...strokeProps} />
      <path d="M3 12h18M12 3c2.5 2.6 2.5 15.4 0 18M12 3c-2.5 2.6-2.5 15.4 0 18" {...strokeProps} />
    </Box>
  );
}

function ShieldIcon() {
  return (
    <Box component="svg" viewBox="0 0 24 24" sx={{ width: 34, height: 34 }}>
      <path d="M12 3 20 6v6c0 4.4-3.3 7.8-8 9-4.7-1.2-8-4.6-8-9V6l8-3Z" {...strokeProps} />
      <path d="m8.8 12 2.2 2.2 4.2-4.4" {...strokeProps} />
    </Box>
  );
}
